import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {FuseConfigService} from '@fuse/services/config.service';
import {fuseAnimations} from '@fuse/animations';
import {ActivatedRoute, Router} from '@angular/router';
import {AccountService} from 'app/core/auth/account.service';
import {ToastrService} from 'ngx-toastr';
import {PASSWORD_POLICY_VIOLATION_TYPE} from 'app/app.constants';
import {NO_PASSWORD_RULES, PasswordPolicyService, PasswordRules} from 'app/core/auth/password-policy.service';
import {evaluatePasswordRules, passwordRulesValidator, PasswordRuleState} from 'app/core/auth/password-rules';

@Component({
    selector: 'reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    resetPasswordForm: UntypedFormGroup;
    key: string;
    tenant: string;
    isSubmitting = false;
    passwordRules: PasswordRules = NO_PASSWORD_RULES;
    requirements: PasswordRuleState[] = [];

    // Private
    private _unsubscribeAll: Subject<void>;

    constructor(
        private router: Router,
        private accountService: AccountService,
        private _toastr: ToastrService,
        private activeRoute: ActivatedRoute,
        private _formBuilder: UntypedFormBuilder,
        private _fuseConfigService: FuseConfigService,
        private passwordPolicyService: PasswordPolicyService
    ) {
        // Configure the layout
        this._fuseConfigService.config = {
            layout: {
                navbar: {
                    hidden: true
                },
                toolbar: {
                    hidden: true
                },
                footer: {
                    hidden: true
                },
                sidepanel: {
                    hidden: true
                }
            }
        };

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.getRouteParams();
        this.resetPasswordForm = this._formBuilder.group({
            name: [''],
            email: ['', [Validators.email]],
            password: ['', [Validators.required, passwordRulesValidator(() => this.passwordRules)]],
            passwordConfirm: ['', [Validators.required, confirmPasswordValidator]]
        });

        this.loadPasswordRules();

        // Update the validity of the 'passwordConfirm' field
        // when the 'password' field changes
        this.resetPasswordForm
            .get('password')
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((password) => {
                this.requirements = evaluatePasswordRules(password, this.passwordRules);
                this.resetPasswordForm.get('passwordConfirm').updateValueAndValidity();
            });
    }

    /**
     * The rules belong to the tenant in the link, and this page has no session, so they come from
     * the public endpoint. They arrive after the form is built, hence the re-validation.
     */
    private loadPasswordRules(): void {
        this.passwordPolicyService
            .getRules(this.tenant)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((rules) => {
                this.passwordRules = rules;
                this.requirements = evaluatePasswordRules(this.resetPasswordForm.get('password').value, rules);
                this.resetPasswordForm.get('password').updateValueAndValidity();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // tslint:disable-next-line:typedef
    getRouteParams() {
        const queryParams = this.activeRoute.snapshot.queryParams;
        this.key = queryParams.key;
        this.tenant = queryParams.tenant;
    }

    /**
     * Reset user's password
     */
    // tslint:disable-next-line:typedef
    resetPassword() {
        if (!this.key || !this.tenant) {
            this._toastr.error('This password reset link is incomplete. Please request a new one.');
            return;
        }

        if (this.resetPasswordForm.invalid || this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        this.accountService.finishPasswordReset(this.tenant, this.key, this.resetPasswordForm.value.password).subscribe(
            () => {
                this.isSubmitting = false;
                this._toastr.info('Password was successfully changed');
                this.router.navigate(['/login']);
            },
            (err) => {
                this.isSubmitting = false;

                // A rejected password is already reported by the error interceptor; toasting here
                // as well would show the same thing twice.
                if (err?.error?.type === PASSWORD_POLICY_VIOLATION_TYPE) {
                    return;
                }

                // The API explains why (link expired, for instance); fall back if it does not.
                this._toastr.error(err?.error?.title ?? 'The operation could not be completed successfully');
            }
        );
    }
}

/**
 * Confirm password validator
 *
 * @param {AbstractControl} control
 * @returns {ValidationErrors | null}
 */
export const confirmPasswordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !control) {
        return null;
    }

    const password = control.parent.get('password');
    const passwordConfirm = control.parent.get('passwordConfirm');

    if (!password || !passwordConfirm) {
        return null;
    }

    if (passwordConfirm.value === '') {
        return null;
    }

    if (password.value === passwordConfirm.value) {
        return null;
    }

    return {passwordsNotMatching: true};
};
